/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";
import PhotoGallery from "../components/PhotoGallery";
import { JsonView, defaultStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

const APPROVAL_OPTIONS = ["approved", "rejected", "needs review"];
const CATEGORY_OPTIONS = ["L1", "L2", "L3", "L4"];
const CALL_STATUS_OPTIONS = ["Connected", "Not Answered"];
const VDB_INTEREST_OPTIONS = ["Yes", "No"];
const SOURCE_CHANNEL_OPTIONS = ["Referral", "Inbound", "Cold Call"];

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
      >
        <option value="">— select —</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function TextField({ label, value, onChange, readOnly }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        readOnly={readOnly}
        className={`w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${readOnly ? "opacity-60 cursor-default" : ""}`}
      />
    </div>
  );
}

export default function ReviewCard({ initialData, onApproved }) {
  const [formData, setFormData] = useState(initialData || {});
  const [changes, setChanges] = useState({});
  const { user } = useAuth();
  const currentUser = JSON.parse(localStorage.getItem("fluxonix_user") || "{}");

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (value !== (initialData[field] ?? "")) {
      setChanges((prev) => ({
        ...prev,
        [field]: { old: initialData[field] ?? "", new: value },
      }));
    } else {
      const next = { ...changes };
      delete next[field];
      setChanges(next);
    }
  };

  const handleSubmit = async () => {
    try {
      const updatedFields = Object.fromEntries(
        Object.entries(changes).map(([field, { new: newVal }]) => [field, newVal])
      );

      const payload = {
        ...updatedFields,
        reviewer_changes: changes,
        approval_status: formData.approval_status || "approved",
        reviewed_by: currentUser?.email,
      };

      if (user?.permissions?.includes("can_review_submissions")) {
        await api.patch(`/admin/submissions/${initialData.id}`, payload);
        await api.post(`/submissions/${initialData.id}/approve`);
      } else {
        await api.patch(`/lpo-submissions/${initialData.id}`, payload);
      }

      if (onApproved) onApproved(initialData.id);
    } catch (err) {
      console.error("Approval failed:", err);
      alert("Error saving changes.");
    }
  };

  const resolvedVoiceUrl = formData?.voice_url?.startsWith("http")
    ? formData.voice_url
    : formData?.voice_url
    ? `/${formData.voice_url}`
    : null;

  const resolvedSupplierName =
    formData.structured_data?.supplier_name ||
    formData.submission_data?.supplier_name ||
    formData.supplier_name ||
    "Unnamed Supplier";

  const resolvedLocation =
    formData.structured_data?.location ||
    formData.submission_data?.location ||
    formData.location ||
    "Unknown Location";

  return (
    <div className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 w-full mb-6 flex flex-col h-full">
      <div className="p-6 flex flex-col flex-1">
        {/* Card header */}
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-primary/10 p-2 rounded-lg flex-shrink-0">
            <span className="material-symbols-outlined text-primary text-base">storefront</span>
          </div>
          <div>
            <p className="text-base font-semibold text-slate-900 dark:text-white leading-snug">
              {resolvedSupplierName}
            </p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{resolvedLocation}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Left column: photo + voice */}
          <div className="md:col-span-4 space-y-4">
            {(formData?.photo_urls?.length > 0 || formData?.photo_url) && (
              <div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Photo
                </span>
                <div className="mt-1.5">
                  <PhotoGallery
                    photoUrls={formData?.photo_urls}
                    singlePhotoUrl={formData?.photo_url}
                  />
                </div>
              </div>
            )}

            {resolvedVoiceUrl && (
              <div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Voice Note
                </span>
                <audio
                  controls
                  src={resolvedVoiceUrl}
                  type="audio/webm"
                  className="w-full mt-1.5 rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Right column: form fields */}
          <div className="md:col-span-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="Supplier Type"
                value={formData.category || ""}
                onChange={(v) => handleChange("category", v)}
                options={CATEGORY_OPTIONS}
              />
              <SelectField
                label="Call Status"
                value={formData.call_status || ""}
                onChange={(v) => handleChange("call_status", v)}
                options={CALL_STATUS_OPTIONS}
              />
              <SelectField
                label="VDB Interest"
                value={formData.vdb_interest || ""}
                onChange={(v) => handleChange("vdb_interest", v)}
                options={VDB_INTEREST_OPTIONS}
              />
              <SelectField
                label="Approval Status"
                value={formData.approval_status || ""}
                onChange={(v) => handleChange("approval_status", v)}
                options={APPROVAL_OPTIONS}
              />
              <TextField
                label="Remarks"
                value={formData.remarks || ""}
                onChange={(v) => handleChange("remarks", v)}
              />
              <SelectField
                label="Source Channel"
                value={formData.source_channel || ""}
                onChange={(v) => handleChange("source_channel", v)}
                options={SOURCE_CHANNEL_OPTIONS}
              />
              {formData.detected_language && (
                <TextField
                  label="Detected Language"
                  value={formData.detected_language}
                  readOnly
                />
              )}
              {formData.confidence_score !== null && formData.confidence_score !== undefined && (
                <TextField
                  label="Confidence Score"
                  value={`${(formData.confidence_score * 100).toFixed(1)} %`}
                  readOnly
                />
              )}
            </div>

            {/* Submission Data JSON */}
            {formData.submission_data && (
              <div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Submission Data
                </span>
                <div className="mt-1.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 max-h-60 overflow-y-auto">
                  <JsonView
                    data={formData.submission_data}
                    shouldInitiallyExpand={(level) => level < 1}
                    style={defaultStyles}
                  />
                </div>
              </div>
            )}

            {/* Raw Transcript */}
            {formData.raw_transcript && (
              <div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Raw Transcript
                </span>
                <pre className="mt-1.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-xs font-mono text-slate-700 dark:text-slate-300 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {formData.raw_transcript}
                </pre>
              </div>
            )}

            {/* Translated Transcript */}
            {formData.translated_transcript && (
              <div>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Translated (English)
                </span>
                <pre className="mt-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs font-mono text-slate-700 dark:text-slate-300 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {formData.translated_transcript}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="mt-auto pt-5 border-t border-slate-100 dark:border-slate-800" />

        {/* Submit button */}
        <button
          type="button"
          onClick={handleSubmit}
          className="w-full py-3 bg-primary text-white rounded-xl font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/30 hover:-translate-y-0.5 transition-all"
        >
          Save &amp; Approve
        </button>
      </div>
    </div>
  );
}
